import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, Target, Users, Briefcase, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export const UeberUns = () => {
  const values = [
    {
      icon: Target,
      title: 'Spezialisierung',
      description: 'Wir konzentrieren uns ausschließlich auf Handelsimmobilien. Diese Fokussierung ermöglicht tiefes Marktverständnis und direkte Kontakte zu den richtigen Käufern.'
    },
    {
      icon: Users,
      title: 'Netzwerk',
      description: 'Durch unsere jahrelange Tätigkeit haben wir ein bundesweites Netzwerk an institutionellen Investoren, Family Offices und Privatinvestoren aufgebaut.'
    },
    {
      icon: Briefcase,
      title: 'Professionalität',
      description: 'Strukturierter Prozess, professionelle Unterlagen und transparente Kommunikation – von der ersten Analyse bis zum Notartermin.'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Über uns – RetailInvest by Heinrichs Immobilien"
        description="RetailInvest ist spezialisiert auf die Vermittlung von Handelsimmobilien in Deutschland. Eine Marke von Heinrichs Immobilien mit Fokus auf strukturierte Prozesse."
        url="/ueber-uns"
      />
      <Navbar />
      <main className="flex-grow">
        {/* Hero */}
        <section className="bg-gradient-to-br from-blue-50 to-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-20 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-6 mx-auto"></div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--navy-900)' }}>
              RetailInvest by Heinrichs Immobilien
            </h1>
            <p className="text-lg leading-relaxed max-w-3xl mx-auto" style={{ color: 'var(--gray-700)' }}>
              Ihr Ansprechpartner für den erfolgreichen Verkauf von Handelsimmobilien in Deutschland.
            </p>
          </div>
        </section>

        {/* Wer wir sind */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-6"></div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: 'var(--navy-900)' }}>
                  Wer wir sind
                </h2>
                <div className="space-y-4" style={{ color: 'var(--gray-700)' }}>
                  <p className="text-lg leading-relaxed">
                    <strong style={{ color: 'var(--navy-800)' }}>RetailInvest</strong> ist eine Marke von <strong style={{ color: 'var(--navy-800)' }}>Heinrichs Immobilien</strong> und konzentriert sich ausschließlich auf den Verkauf von Handelsimmobilien.
                  </p>
                  <p>
                    Während viele Makler alle Immobilientypen anbieten, haben wir uns bewusst für die <strong>Spezialisierung auf Handelsimmobilien</strong> entschieden. 
                    Diese Fokussierung ermöglicht uns ein tiefes Verständnis für:
                  </p>
                  <ul className="space-y-2 ml-6">
                    <li className="flex items-start gap-2">
                      <span className="text-[#D4A853] font-bold">•</span>
                      <span>Die spezifischen Anforderungen institutioneller Käufer an Handelsimmobilien</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#D4A853] font-bold">•</span>
                      <span>Die Bewertungslogik von Lebensmittelmärkten, Fachmärkten und Fachmarktzentren</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#D4A853] font-bold">•</span>
                      <span>Die Besonderheiten von Mietverträgen im Handelsbereich</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#D4A853] font-bold">•</span>
                      <span>Die aktuellen Marktbewegungen und Käuferprioriäten</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div>
                <img
                  src="https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=compress&cs=tinysrgb&w=800"
                  alt="Professionelles Business Meeting"
                  className="rounded-xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Andreas Heinrichs Profile */}
        <section className="py-20" style={{ background: 'linear-gradient(to bottom, white 0%, #f9fafb 100%)' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Ihr Ansprechpartner
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-12 items-start">
              {/* Andreas Photo & Contact */}
              <div className="text-center lg:text-left">
                <div className="inline-block relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#D4A853]/20 to-[#F0C870]/20 rounded-2xl blur-2xl"></div>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_commprop-advisor/artifacts/zb797gk8_Andreas%20Heinrichs%20Hochkant%20Webseite.png" 
                    alt="Andreas Heinrichs, Geschäftsführer Heinrichs Immobilien – Spezialist für Handelsimmobilien seit 2015"
                    className="relative w-64 h-auto rounded-2xl shadow-2xl ring-4 ring-white"
                  />
                </div>
                <div className="mt-6">
                  <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--navy-900)' }}>
                    Andreas Heinrichs
                  </h3>
                  <p className="text-lg font-semibold mb-4" style={{ color: 'var(--gold-600)' }}>
                    Geschäftsführer & Spezialist für Einzelhandelsimmobilien
                  </p>
                  <Link to="/kontakt">
                    <Button className="btn-gold-gradient text-white border-0 font-semibold">
                      Kontakt aufnehmen
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Andreas Profile Content */}
              <div className="space-y-6" style={{ color: 'var(--gray-700)' }}>
                <div>
                  <h4 className="text-xl font-bold mb-3" style={{ color: 'var(--navy-800)' }}>
                    Warum mit mir arbeiten?
                  </h4>
                  <p className="text-lg leading-relaxed">
                    Seit über <strong style={{ color: 'var(--gold-600)' }}>8 Jahren</strong> berate ich Eigentümer beim Verkauf von Handelsimmobilien. 
                    In dieser Zeit habe ich gelernt: <strong>Jeder Verkauf ist anders</strong> – aber der Prozess muss strukturiert sein. 
                    Mein Ziel ist es, für Sie den besten Käufer zu finden und dabei jeden Schritt transparent zu gestalten.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="p-5 bg-white border-l-4" style={{ borderColor: 'var(--gold-600)' }}>
                    <div className="text-3xl font-bold mb-1" style={{ color: 'var(--navy-900)' }}>25+</div>
                    <div className="text-sm font-semibold" style={{ color: 'var(--gray-600)' }}>Verkaufte Objekte</div>
                  </Card>
                  <Card className="p-5 bg-white border-l-4" style={{ borderColor: 'var(--gold-600)' }}>
                    <div className="text-3xl font-bold mb-1" style={{ color: 'var(--navy-900)' }}>€ 75+ Mio.</div>
                    <div className="text-sm font-semibold" style={{ color: 'var(--gray-600)' }}>Transaktionsvolumen</div>
                  </Card>
                  <Card className="p-5 bg-white border-l-4" style={{ borderColor: 'var(--gold-600)' }}>
                    <div className="text-3xl font-bold mb-1" style={{ color: 'var(--navy-900)' }}>8 Jahre</div>
                    <div className="text-sm font-semibold" style={{ color: 'var(--gray-600)' }}>Erfahrung im Handel</div>
                  </Card>
                  <Card className="p-5 bg-white border-l-4" style={{ borderColor: 'var(--gold-600)' }}>
                    <div className="text-3xl font-bold mb-1" style={{ color: 'var(--navy-900)' }}>100%</div>
                    <div className="text-sm font-semibold" style={{ color: 'var(--gray-600)' }}>Zufriedene Mandanten</div>
                  </Card>
                </div>

                <div>
                  <h4 className="text-xl font-bold mb-3" style={{ color: 'var(--navy-800)' }}>
                    Mein Werdegang
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="text-[#D4A853] font-bold text-xl">•</span>
                      <span><strong>Seit 2023:</strong> Geschäftsführer RetailInvest by Heinrichs Immobilien</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#D4A853] font-bold text-xl">•</span>
                      <span><strong>2015-2023:</strong> Senior Consultant bei führendem Gewerbeimmobilienmakler in NRW</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#D4A853] font-bold text-xl">•</span>
                      <span><strong>2012-2015:</strong> Studium Immobilienwirtschaft (B.Sc.) mit Schwerpunkt Investment</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#D4A853] font-bold text-xl">•</span>
                      <span><strong>Spezialisierung:</strong> Lebensmittelmärkte, Fachmärkte, Fachmarktzentren</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-100">
                  <h4 className="text-lg font-bold mb-3" style={{ color: 'var(--navy-800)' }}>
                    💡 Meine Philosophie
                  </h4>
                  <p className="italic leading-relaxed">
                    "Ein erfolgreicher Verkauf beginnt nicht mit dem Exposé, sondern mit der richtigen Vorbereitung. 
                    Wer seine Unterlagen im Griff hat, seinen WAULT kennt und den Markt versteht, verkauft 30-40% schneller – 
                    und erzielt bessere Preise. Genau dabei helfe ich Ihnen."
                  </p>
                  <p className="mt-3 font-semibold">— Andreas Heinrichs</p>
                </div>

                <div className="pt-6">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_commprop-advisor/artifacts/n84sgi4h_IMG_2297.jpeg" 
                    alt="Andreas Heinrichs in der Beratung"
                    className="w-full h-auto rounded-xl shadow-lg"
                  />
                  <p className="text-sm text-center mt-3 italic" style={{ color: 'var(--gray-600)' }}>
                    Andreas Heinrichs in der Beratung – persönlich, strukturiert, lösungsorientiert
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Unsere Werte
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {values.map((value, index) => {
                const IconComponent = value.icon;
                return (
                  <Card key={index} className="glass-card p-8 text-center">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg" style={{ background: 'linear-gradient(135deg, #D4A853 0%, #F0C870 100%)' }}>
                      <IconComponent className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>
                      {value.title}
                    </h3>
                    <p style={{ color: 'var(--gray-700)' }}>
                      {value.description}
                    </p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Lernen Sie uns kennen
            </h2>
            <p className="text-lg mb-8 opacity-90">
              Vereinbaren Sie ein unverbindliches Gespräch und erfahren Sie, wie wir Sie beim Verkauf unterstützen können.
            </p>
            <Link to="/kontakt">
              <Button className="btn-gold-gradient text-white border-0 font-semibold px-10 py-7 text-lg shadow-2xl">
                Jetzt Kontakt aufnehmen
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default UeberUns;